import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type {
  CreateLandingPageRequest,
  DeleteLandingPageResult,
  LandingAIAccepted,
  LandingAIConversation,
  LandingAIMessage,
  LandingAIRequest,
  LandingDeletePreview,
  LandingImportReport,
  LandingLead,
  LandingMediaItem,
  LandingNavigation,
  LandingNavRequest,
  LandingPage,
  LandingTemplate,
  LandingVersion,
  SaveLandingTemplateRequest,
  UpdateLandingPageRequest,
  UpdateLandingTemplateMetaRequest,
} from '@/types/landing';
import type { LandingRuntime } from '@/utils/landing';

const normalizeNav = (n: LandingNavigation | null): LandingNavigation => ({
  daily: n?.daily ?? [],
  by_source: n?.by_source ?? [],
  by_page: n?.by_page ?? [],
});

/** Landing pages — ver docs/API.md do marketingbackgo, seção "Landing pages". */
class LandingService {
  private runtimeCache: Promise<LandingRuntime> | null = null;

  private async api() {
    return authService.getApiClient();
  }

  /** CSS e JS dos blocos (carrossel, contagem, formulário) para o canvas e as prévias; uma vez por sessão. */
  runtime(): Promise<LandingRuntime> {
    if (!this.runtimeCache) {
      this.runtimeCache = this.api()
        .then((api) => api.get('/landing-pages/runtime'))
        .then((res) => readData<LandingRuntime>(res, 'Falha ao carregar o estilo dos blocos'))
        .catch((err) => {
          this.runtimeCache = null;
          throw err;
        });
    }
    return this.runtimeCache;
  }

  async list(): Promise<LandingPage[]> {
    const res = await (await this.api()).get('/landing-pages');
    return readList<LandingPage>(res, 'Falha ao carregar as landing pages');
  }

  async get(id: number): Promise<LandingPage> {
    const res = await (await this.api()).get(`/landing-pages/${id}`);
    return readData<LandingPage>(res, 'Falha ao carregar a landing page');
  }

  async create(req: CreateLandingPageRequest): Promise<LandingPage> {
    const res = await (await this.api()).post('/landing-pages', req);
    return readData<LandingPage>(res, 'Falha ao criar a landing page');
  }

  async update(id: number, req: UpdateLandingPageRequest): Promise<LandingPage> {
    const res = await (await this.api()).put(`/landing-pages/${id}`, req);
    return readData<LandingPage>(res, 'Falha ao salvar as configurações');
  }

  /** Grava o rascunho; devolve a versão (nova, ou a atual se o HTML não mudou). */
  async saveDraft(id: number, html: string, editorData: string): Promise<LandingVersion> {
    const res = await (await this.api()).put(`/landing-pages/${id}/draft`, { html, editor_data: editorData });
    return readData<LandingVersion>(res, 'Falha ao salvar o rascunho');
  }

  async versions(id: number): Promise<LandingVersion[]> {
    const res = await (await this.api()).get(`/landing-pages/${id}/versions`);
    return readList<LandingVersion>(res, 'Falha ao carregar as versões');
  }

  async version(id: number, versionId: number): Promise<LandingVersion> {
    const res = await (await this.api()).get(`/landing-pages/${id}/versions/${versionId}`);
    return readData<LandingVersion>(res, 'Falha ao carregar a versão');
  }

  /** Volta a uma versão (cria uma versão nova com o conteúdo dela). */
  async restoreVersion(id: number, versionId: number): Promise<LandingVersion> {
    const res = await (await this.api()).post(`/landing-pages/${id}/versions/${versionId}/restore`, {});
    return readData<LandingVersion>(res, 'Falha ao voltar para a versão');
  }

  /** Desfaz a última alteração (volta ao conteúdo anterior ao da versão atual). */
  async undo(id: number): Promise<LandingVersion> {
    const res = await (await this.api()).post(`/landing-pages/${id}/undo`, {});
    return readData<LandingVersion>(res, 'Não foi possível desfazer');
  }

  async aiConversations(id: number): Promise<LandingAIConversation[]> {
    const res = await (await this.api()).get(`/landing-pages/${id}/ai/conversations`);
    return readList<LandingAIConversation>(res, 'Falha ao carregar as conversas');
  }

  async aiMessages(id: number, conversationId: number): Promise<LandingAIMessage[]> {
    const res = await (await this.api()).get(`/landing-pages/${id}/ai/conversations/${conversationId}/messages`);
    return readList<LandingAIMessage>(res, 'Falha ao carregar a conversa');
  }

  /** Envia o pedido ao assistente; a resposta chega depois nas mensagens da conversa. */
  async aiAsk(id: number, req: LandingAIRequest): Promise<LandingAIAccepted> {
    const res = await (await this.api()).post(`/landing-pages/${id}/ai/messages`, req);
    return readData<LandingAIAccepted>(res, 'Falha ao enviar o pedido ao assistente');
  }

  async publish(id: number): Promise<{ url: string }> {
    const res = await (await this.api()).post(`/landing-pages/${id}/publish`, {});
    return readData<{ url: string }>(res, 'Falha ao publicar');
  }

  async publishedHtml(id: number): Promise<string> {
    const res = await (await this.api()).get(`/landing-pages/${id}/published-html`);
    return (await readData<{ html: string }>(res, 'Sem versão publicada')).html;
  }

  async leads(id: number, afterId?: number, limit = 100): Promise<LandingLead[]> {
    const res = await (await this.api()).get(`/landing-pages/${id}/leads${toQuery({ after_id: afterId, limit })}`);
    return readList<LandingLead>(res, 'Falha ao carregar os leads');
  }

  /** id ausente = consolidado de todas as páginas. */
  async navigation(id: number | null, days: number): Promise<LandingNavigation> {
    const base = id ? `/landing-pages/${id}/navigation` : '/landing-pages/navigation';
    const res = await (await this.api()).get(`${base}${toQuery({ days })}`);
    return normalizeNav(await readData<LandingNavigation>(res, 'Falha ao carregar a navegação'));
  }

  async navRequests(id: number | null, afterId?: number, limit = 100): Promise<LandingNavRequest[]> {
    const base = id ? `/landing-pages/${id}/navigation/requests` : '/landing-pages/navigation/requests';
    const res = await (await this.api()).get(`${base}${toQuery({ after_id: afterId, limit })}`);
    return readList<LandingNavRequest>(res, 'Falha ao carregar os acessos');
  }

  /** Prévia da exclusão: mídias da página e quais podem sair junto. */
  async deletePreview(id: number): Promise<LandingDeletePreview> {
    const res = await (await this.api()).get(`/landing-pages/${id}/delete-preview`);
    const p = await readData<LandingDeletePreview>(res, 'Falha ao preparar a exclusão');
    return { ...p, thank_you_of: p.thank_you_of ?? [], media: p.media ?? [] };
  }

  /** Exclui a página (sai do ar) e as mídias escolhidas. */
  async remove(id: number, deleteMediaIds: number[]): Promise<DeleteLandingPageResult> {
    const res = await (await this.api()).post(`/landing-pages/${id}/delete`, { delete_media_ids: deleteMediaIds });
    const r = await readData<DeleteLandingPageResult>(res, 'Falha ao excluir a landing page');
    return { media_deleted: r.media_deleted ?? [], media_kept: r.media_kept ?? [], warnings: r.warnings ?? [] };
  }

  async pageMedia(id: number): Promise<LandingMediaItem[]> {
    const res = await (await this.api()).get(`/landing-pages/${id}/media`);
    return readList<LandingMediaItem>(res, 'Falha ao carregar as mídias da página');
  }

  /** Gera (ou reaproveita) o endereço público da mídia da Biblioteca para usar na página. */
  async useMedia(id: number, mediaId: number): Promise<LandingMediaItem> {
    const res = await (await this.api()).post(`/landing-pages/${id}/media`, { media_id: mediaId });
    return readData<LandingMediaItem>(res, 'Falha ao preparar a mídia para a página');
  }

  /** Só o endereço público da mídia (editor de templates: não há página para registrar o uso). */
  async publicMedia(mediaId: number): Promise<LandingMediaItem> {
    const res = await (await this.api()).post('/landing-pages/public-media', { media_id: mediaId });
    return readData<LandingMediaItem>(res, 'Falha ao preparar a mídia');
  }

  async pagesUsingMedia(mediaId: number): Promise<LandingPage[]> {
    const res = await (await this.api()).get(`/landing-pages/by-media/${mediaId}`);
    return readList<LandingPage>(res, 'Falha ao carregar as landing pages da mídia');
  }

  async templates(full = false): Promise<LandingTemplate[]> {
    const res = await (await this.api()).get(`/landing-page-templates${toQuery({ full })}`);
    return readList<LandingTemplate>(res, 'Falha ao carregar os templates');
  }

  async template(id: number): Promise<LandingTemplate> {
    const res = await (await this.api()).get(`/landing-page-templates/${id}`);
    return readData<LandingTemplate>(res, 'Falha ao carregar o template');
  }

  /** Muda só nome, descrição, categoria, ordem e situação (sem reenviar o conteúdo). */
  async updateTemplateMeta(id: number, req: UpdateLandingTemplateMetaRequest): Promise<LandingTemplate> {
    const res = await (await this.api()).put(`/landing-page-templates/${id}/meta`, req);
    return readData<LandingTemplate>(res, 'Falha ao salvar o template');
  }

  async saveTemplate(id: number | null, req: SaveLandingTemplateRequest): Promise<LandingTemplate> {
    const api = await this.api();
    const res = id ? await api.put(`/landing-page-templates/${id}`, req) : await api.post('/landing-page-templates', req);
    return readData<LandingTemplate>(res, 'Falha ao salvar o template');
  }

  async lastImport(): Promise<LandingImportReport | null> {
    const res = await (await this.api()).get('/landing-pages/import');
    const r = await readData<LandingImportReport | null>(res, 'Falha ao ler a última importação');
    return r ? { ...r, problems: r.problems ?? [] } : null;
  }

  async startImport(landingPageId: number, dryRun: boolean): Promise<void> {
    const res = await (await this.api()).post('/landing-pages/import', { landing_page_id: landingPageId, dry_run: dryRun });
    await ensureOk(res, 'Falha ao iniciar a importação');
  }
}

const landingService = new LandingService();
export default landingService;
